import { motion, type Easing, useMotionValue, useSpring } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useRef, useState, useEffect } from "react";
import { TypingText } from "./TypingText";

const easeOut: Easing = [0.16, 1, 0.3, 1];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: easeOut,
    },
  },
};

export const HeroSection = () => {
  const { t } = useLanguage();
  const sectionRef = useRef<HTMLElement>(null);
  const lastYRef = useRef<number | null>(null);

  const [windowWidth, setWindowWidth] = useState(0);

  useEffect(() => {
    setWindowWidth(window.innerWidth);
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Motion values for smooth phone animation
  const leftPhoneY = useMotionValue(0);
  const rightPhoneY = useMotionValue(0);
  const leftPhoneSpring = useSpring(leftPhoneY, { stiffness: 150, damping: 15 });
  const rightPhoneSpring = useSpring(rightPhoneY, { stiffness: 150, damping: 15 });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const currentY = e.clientY;
    if (lastYRef.current !== null) {
      const deltaY = currentY - lastYRef.current;
      const currentLeft = leftPhoneY.get();
      const currentRight = rightPhoneY.get();
      leftPhoneY.set(Math.max(-10, Math.min(10, currentLeft - deltaY * 0.1)));
      rightPhoneY.set(Math.max(-10, Math.min(10, currentRight + deltaY * 0.1)));
    }
    lastYRef.current = currentY;
  };

  const handleMouseLeave = () => {
    leftPhoneY.set(0);
    rightPhoneY.set(0);
    lastYRef.current = null;
  };

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex flex-col bg-transparent overflow-hidden"
      style={{ zIndex: 10 }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 flex-1 flex flex-col justify-center max-w-7xl mx-auto px-6 pt-32 pb-5 lg:pt-40 lg:pb-32 w-full"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left: Text Content */}
          <div className="max-w-3xl lg:max-w-none">
            {/* Main Headline with Typing Effect */}
            <motion.h1
              variants={itemVariants}
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground leading-[1.2] tracking-tight sm:whitespace-nowrap min-h-[2.4em] sm:min-h-0"
              style={{
                textShadow: '0 2px 12px rgba(0, 0, 0, 0.5)',
                overflow: 'visible'
              }}
            >
              <span className="sm:inline block">{t.hero.typingPrefix}</span>
              <TypingText words={t.hero.typingWords} />
            </motion.h1>

            {/* Result line */}
            <motion.p
              variants={itemVariants}
              className="mt-6 lg:mt-8 text-2xl sm:text-3xl lg:text-4xl font-semibold text-foreground leading-[1.2] xl:whitespace-nowrap"
              style={{
                textShadow: '0 2px 12px rgba(0, 0, 0, 0.5)'
              }}
            >
              {t.hero.resultLine}
            </motion.p>

            {/* Subheadline */}
            <motion.p
              variants={itemVariants}
              className="mt-6 lg:mt-8 text-base lg:text-lg max-w-xl leading-relaxed"
              style={{
                color: 'rgba(255,255,255,0.85)',
                fontWeight: 500,
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.6)',
                lineHeight: '1.8'
              }}
            >
              {t.hero.subheadline}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              variants={itemVariants}
              className="mt-10 lg:mt-12 flex flex-col sm:flex-row gap-4"
            >
              <Button
                variant="heroPrimary"
                size="xl"
                aria-label={t.hero.cta.primary}
                className="text-white border border-white hover:[background:white!important] hover:text-black transition-all duration-300 group font-bold text-base"
                style={{ background: "linear-gradient(135deg, #000000 50%, #00d4ff40 100%)" }}
                onClick={() => {
                  document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                {t.hero.cta.primary}
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="transition-all duration-300 group-hover:translate-x-1"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </Button>
            </motion.div>

            {/* Trust Indicators - Horizontal */}
            <motion.div
              variants={itemVariants}
              className="mt-12 lg:mt-16 grid grid-cols-2 gap-x-8 gap-y-4 max-w-xl"
            >
              {t.hero.trustIndicators.map((indicator, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2"
                >
                  <Check className="w-4 h-4 text-gray-400" strokeWidth={2.5} />
                  <span
                    className="text-sm"
                      style={{
                        color: 'rgba(255,255,255,0.85)',
                        fontWeight: 500,
                        textShadow: '0 1px 6px rgba(0, 0, 0, 0.5)'
                      }}
                  >
                    {indicator}
                  </span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: Phone Mockups */}
          <motion.div
            variants={itemVariants}
            style={{
              width: windowWidth >= 1560 ? '36vw' : windowWidth >= 1440 ? '35vw' : windowWidth >= 1280 ? '37vw' : windowWidth >= 1024 ? '38vw' : windowWidth >= 768 ? '66vw' : windowWidth >= 640 ? '68vw' : windowWidth >= 375 ? '76vw'   : '65vw',
              maxWidth: '780px',
              height: windowWidth >= 1024 ? '70vh' : windowWidth >= 768 ? '55vw' : '90vw',
              maxHeight: '600px',
              marginRight: windowWidth >= 1024 ? '3vw' : 'auto',
              marginLeft: windowWidth >= 1024 ? '8vw' : 'auto',
              justifyContent: 'center',
              marginTop: windowWidth >= 768 && windowWidth < 1024 ? '4rem' : windowWidth >= 1024 && windowWidth < 1330 ? '-5vh' : '0',
            }}
            className="flex items-center overflow-visible"
          >
            {/* Phone 1 - left, overlapping right phone */}
            <motion.img
              src="/images/phone-1-400w.webp"
              srcSet="/images/phone-1-330w.webp 330w, /images/phone-1-400w.webp 400w"
              sizes="(max-width: 768px) 60vw, 400px"
              width={400}
              height={692}
              alt="AiLum Solutions Mobile App"
              fetchPriority="high"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3, ease: easeOut }}
              style={{
                y: leftPhoneSpring,
                width: windowWidth >= 1024 ? '55%' : windowWidth >= 768 ? '50%' : '60%',
                height: 'auto',
                flexShrink: 0,
                zIndex: 20,
              }}
              className="object-contain drop-shadow-2xl"
            />

            {/* Phone 2 - right, behind left phone */}
            <motion.img
              src="/images/phone-2-400w.webp"
              srcSet="/images/phone-2-330w.webp 330w, /images/phone-2-400w.webp 400w"
              sizes="(max-width: 768px) 60vw, 400px"
              width={400}
              height={659}
              alt="AiLum Solutions Features"
              fetchPriority="high"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.5, ease: easeOut }}
              style={{
                y: rightPhoneSpring,
                width: windowWidth >= 1024 ? '55%' : windowWidth >= 768 ? '50%' : '60%',
                height: 'auto',
                flexShrink: 0,
                marginLeft: '-15%',
                marginTop: '8%',
                zIndex: 10,
              }}
              className="object-contain drop-shadow-2xl"
            />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
